import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link2, Settings, Trash2, Plus, ArrowUp, ArrowDown } from 'lucide-react';

export const APP_LINKS = [
  { label: 'Startfenster', href: '/' },
  { label: 'Unterrichtsverwaltung', href: '/unterrichtsverwaltung' },
  { label: 'Anwesenheitslisten', href: '/anwesenheitslisten' },
  { label: 'Schüler', href: '/students' },
  { label: 'Familien', href: '/families' },
  { label: 'Lehrer', href: '/teachers' },
  { label: 'Standorte & Räume', href: '/locations' },
  { label: 'Raumfinder', href: '/room-finder' },
  { label: 'Kurse', href: '/courses' },
  { label: 'Freie Plätze', href: '/free-slots' },
  { label: 'Kalender', href: '/calendar' },
  { label: 'Konferenzen', href: '/conferences' },
  { label: 'Gebührenverwaltung', href: '/tariffs-discounts' },
  { label: 'Rechnungen', href: '/invoices' },
  { label: 'Rechnungslauf-Vorschau', href: '/billing-preview' },
  { label: 'Fixbeträge', href: '/fixed-amounts' },
  { label: 'Prepaid-Karten', href: '/prepaid-cards' },
  { label: 'Instrumentenvermietung', href: '/instrument-rental' },
  { label: 'Probestunden', href: '/trial-lessons' },
  { label: 'Zahlungspflichtige', href: '/guardians' },
  { label: 'Zahlungspflichtige', href: '/payer-accounts' },
  { label: 'Zahlungsverkehr', href: '/sepa' },
  { label: 'Reports', href: '/reports' },
  { label: 'Nachrichten', href: '/nachrichten' },
  { label: 'Self-Service-Inbox', href: '/self-service-inbox' },
  { label: 'Online-Anmeldungen', href: '/registrations-inbox' },
  { label: 'E-Mail', href: '/email' },
  { label: 'Stammdaten', href: '/stammdaten' },
  { label: 'Einstellungen', href: '/settings' },
];

export const useQuicklinks = () => {
  const [quicklinks, setQuicklinks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/auth/me/quicklinks')
      .then((r) => setQuicklinks(r.data.quicklinks || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async (links) => {
    const r = await api.put('/auth/me/quicklinks', { quicklinks: links });
    setQuicklinks(r.data.quicklinks);
  };

  return { quicklinks, loaded, save };
};

export const QuicklinksEditDialog = ({ open, onOpenChange, quicklinks, onSave }) => {
  const [items, setItems] = useState(quicklinks);
  const [pick, setPick] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customHref, setCustomHref] = useState('');

  useEffect(() => { if (open) setItems(quicklinks); }, [open, quicklinks]);

  const addPick = () => {
    const link = APP_LINKS.find((l) => l.href === pick);
    if (link && !items.some((i) => i.href === link.href)) {
      setItems([...items, { ...link }]);
    }
    setPick('');
  };

  const addCustom = () => {
    const href = customHref.trim();
    const label = customLabel.trim();
    if (!label || !href.startsWith('/')) {
      toast.error('Bezeichnung angeben und Pfad muss mit / beginnen');
      return;
    }
    setItems([...items, { label, href }]);
    setCustomLabel('');
    setCustomHref('');
  };

  const move = (idx, dir) => {
    const next = [...items];
    const t = next[idx];
    next[idx] = next[idx + dir];
    next[idx + dir] = t;
    setItems(next);
  };

  const saveAll = async () => {
    try {
      await onSave(items);
      toast.success('Quicklinks gespeichert');
      onOpenChange(false);
    } catch {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quicklinks bearbeiten</DialogTitle>
          <DialogDescription>
            Definiere Deine eigenen Schnellzugriffe für die linke Spalte der Unterrichtsverwaltung.
            Ohne Quicklinks wird die Spalte ausgeblendet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1" data-testid="quicklinks-list">
          {items.length === 0 && (
            <div className="text-sm text-slate-400 py-2">Keine Quicklinks definiert</div>
          )}
          {items.map((item, idx) => (
            <div key={`${item.href}-${idx}`} className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid={`quicklink-item-${idx}`}>
              <Link2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-800 truncate">{item.label}</span>
              <span className="text-xs text-slate-400 font-mono truncate">{item.href}</span>
              <span className="flex-1" />
              <button onClick={() => idx > 0 && move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => idx < items.length - 1 && move(idx, 1)} disabled={idx === items.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600" data-testid={`quicklink-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-3 space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="quicklinks-page-select"
            >
              <option value="">Seite auswählen…</option>
              {APP_LINKS.filter((l) => !items.some((i) => i.href === l.href)).map((l) => (
                <option key={l.href} value={l.href}>{l.label}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={addPick} disabled={!pick} data-testid="quicklinks-add-page-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Eigene Bezeichnung" className="h-9 text-sm" data-testid="quicklinks-custom-label" />
            <Input value={customHref} onChange={(e) => setCustomHref(e.target.value)} placeholder="/pfad/zur/seite" className="h-9 text-sm font-mono" data-testid="quicklinks-custom-href" />
            <Button variant="outline" size="sm" onClick={addCustom} data-testid="quicklinks-add-custom-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Tipp: Über „Eigene Bezeichnung + Pfad" kannst Du jeden beliebigen Link hinterlegen, z.B. <span className="font-mono">/payer-accounts?filter=sepa-gap</span>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={saveAll} data-testid="quicklinks-save-btn">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const QuicklinksRail = ({ quicklinks, onEdit }) => {
  const navigate = useNavigate();
  if (!quicklinks.length) return null;
  return (
    <div className="w-48 bg-slate-100 border-r border-slate-200 flex flex-col overflow-y-auto" data-testid="quicklinks-rail">
      <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase">Quicklinks</span>
        <button onClick={onEdit} className="text-slate-400 hover:text-slate-700" title="Quicklinks bearbeiten" data-testid="quicklinks-edit-btn">
          <Settings className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2 space-y-1 flex-1">
        {quicklinks.map((l, idx) => (
          <button
            key={`${l.href}-${idx}`}
            onClick={() => navigate(l.href)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded text-left"
            data-testid={`quicklink-nav-${idx}`}
          >
            <Link2 className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{l.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
